// components/parametres/TarifsTab.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ESPECES = ['BOVIN', 'OVIN', 'CAPRIN', 'PORCIN', 'CAMELIN'];

const TarifsTab = () => {
  const [abattoirs, setAbattoirs]     = useState([]);
  const [abattoirSel, setAbattoirSel] = useState('');
  const [typeSel, setTypeSel]         = useState('STANDARD');
  const [especes, setEspeces]         = useState([]);
  const [form, setForm]               = useState({});      // { BOVIN: { stabulation, abattage, ressuage }, ... }
  const [vf, setVf]                   = useState('0');     // viande foraine
  const [chargement, setChargement]   = useState(false);
  const [sauvegarde, setSauvegarde]   = useState(false);
  const [message, setMessage]         = useState(null);

  // Charger abattoirs et espèces
  useEffect(() => {
    const init = async () => {
      try {
        const [resA, resE] = await Promise.all([
          api.get('/abattoirs'),
          api.get('/parametres/especes'),
        ]);
        setAbattoirs(resA.data);
        setEspeces(resE.data);
        if (resA.data.length > 0) setAbattoirSel(String(resA.data[0].id));
      } catch (err) {
        setMessage({ type: 'erreur', texte: 'Erreur de chargement.' });
      }
    };
    init();
  }, []);

  // Charger tarifs quand abattoir change
  useEffect(() => {
    if (!abattoirSel || especes.length === 0) return;
    const charger = async () => {
      setChargement(true);
      setMessage(null);
      try {
        const res = await api.get(`/parametres/tarifs/${abattoirSel}`);
        // Construire form depuis les données
        const newForm = {};
        especes.forEach(e => {
          const std = res.data.tarifs.find(t => t.espece_code === e.code && t.type_tarif === 'STANDARD');
          const spe = res.data.tarifs.find(t => t.espece_code === e.code && t.type_tarif === 'SPECIAL');
          newForm[e.code] = {
            STANDARD: {
              stabulation: std ? String(std.stabulation) : '0',
              abattage:    std ? String(std.abattage)    : '0',
              ressuage:    std ? String(std.ressuage)    : '0',
              ressuage_unite: std ? (std.ressuage_unite || 'kg') : 'kg',
            },
            SPECIAL: {
              stabulation: spe ? String(spe.stabulation) : '0',
              abattage:    spe ? String(spe.abattage)    : '0',
              ressuage:    spe ? String(spe.ressuage)    : '0',
              ressuage_unite: spe ? (spe.ressuage_unite || 'kg') : 'kg',
            },
          };
        });
        setForm(newForm);
        setVf(String(res.data.viande_foraine));
      } catch (err) {
        setMessage({ type: 'erreur', texte: 'Erreur de chargement des tarifs.' });
      } finally {
        setChargement(false);
      }
    };
    charger();
  }, [abattoirSel, especes]);

  const handleChange = (espece_code, champ, valeur) => {
    setForm(prev => ({
      ...prev,
      [espece_code]: {
        ...prev[espece_code],
        [typeSel]: {
          ...prev[espece_code]?.[typeSel],
          [champ]: valeur
        }
      }
    }));
  };

  const sauvegarder = async () => {
    setSauvegarde(true);
    setMessage(null);
    try {
      // Construire la liste des tarifs à envoyer
      const tarifsPayload = [];
      especes.forEach(e => {
        ['STANDARD', 'SPECIAL'].forEach(type => {
          const f = form[e.code]?.[type] || {};
          tarifsPayload.push({
            espece_id:   e.id,
            type_tarif:  type,
            stabulation: parseFloat(f.stabulation) || 0,
            abattage:    parseFloat(f.abattage)    || 0,
            ressuage:    parseFloat(f.ressuage)    || 0,
            ressuage_unite: f.ressuage_unite || 'kg',
          });
        });
      });
      await api.post('/parametres/tarifs', {
        abattoir_id:    parseInt(abattoirSel),
        tarifs:         tarifsPayload,
        viande_foraine: parseFloat(vf) || 0,
      });
      setMessage({ type: 'succes', texte: '✅ Tarifs sauvegardés avec succès !' });
    } catch (err) {
      setMessage({ type: 'erreur', texte: err.response?.data?.message || 'Erreur serveur.' });
    } finally {
      setSauvegarde(false);
    }
  };

  const abattoirNom = abattoirs.find(a => String(a.id) === abattoirSel)?.nom || '';

  const styles = {
    container:   { background: 'white', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    topBar:      { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
    label:       { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' },
    select:      { padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, minWidth: 200 },
    typeBtns:    { display: 'flex', gap: 8 },
    btnType:     (active) => ({
      padding: '8px 20px', borderRadius: 6, border: '2px solid', cursor: 'pointer', fontWeight: 700, fontSize: 14,
      background: active ? '#1a5c2a' : 'white',
      color:      active ? 'white'   : '#1a5c2a',
      borderColor:'#1a5c2a',
    }),
    table:       { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th:          { background: '#f3f4f6', padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb' },
    td:          { padding: '10px 14px', borderBottom: '1px solid #f3f4f6' },
    input:       { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, width: 100, fontSize: 14, textAlign: 'right' },
    badge:       (color) => ({ display:'inline-block', padding:'2px 10px', borderRadius:12, fontSize:12, fontWeight:700, background: color+'22', color }),
    total:       { fontWeight: 700, color: '#1a5c2a' },
    vfRow:       { marginTop: 20, padding: '16px', background: '#fef3c7', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 16 },
    vfLabel:     { fontSize: 14, fontWeight: 700, color: '#92400e' },
    btnSave:     { marginTop: 24, padding: '10px 32px', background: '#1a5c2a', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    msgSucces:   { marginBottom: 16, padding: '10px 16px', background: '#d1fae5', color: '#065f46', borderRadius: 6, fontSize: 14 },
    msgErreur:   { marginBottom: 16, padding: '10px 16px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 14 },
    infoBar:     { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '10px 16px', fontSize: 13, color: '#1e40af', marginBottom: 16 },
  };

  const espaceColors = { BOVIN:'#6366f1', OVIN:'#10b981', CAPRIN:'#f59e0b', PORCIN:'#ef4444', CAMELIN:'#8b5cf6' };

  return (
    <div style={styles.container}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#1a5c2a' }}>
        💰 Tarification par abattoir
      </h2>

      {message && (
        <div style={message.type === 'succes' ? styles.msgSucces : styles.msgErreur}>
          {message.texte}
        </div>
      )}

      {/* Sélecteur abattoir + type */}
      <div style={styles.topBar}>
        <div>
          <label style={styles.label}>Abattoir (région)</label>
          <select style={styles.select} value={abattoirSel}
            onChange={e => { setAbattoirSel(e.target.value); setMessage(null); }}>
            {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
          </select>
        </div>
        <div>
          <label style={styles.label}>Type de tarification</label>
          <div style={styles.typeBtns}>
            {['STANDARD', 'SPECIAL'].map(t => (
              <button key={t} style={styles.btnType(typeSel === t)} onClick={() => setTypeSel(t)}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.infoBar}>
        ℹ️ Vous modifiez les tarifs <strong>{typeSel}</strong> de l'<strong>{abattoirNom}</strong>.
        Ces tarifs seront automatiquement appliqués lors de la facturation pour cet abattoir.
      </div>

      {chargement ? (
        <p style={{ color: '#6b7280' }}>Chargement des tarifs...</p>
      ) : (
        <>
          {/* Tableau espèces */}
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Espèce</th>
                <th style={styles.th}>Stabulation (FCFA/tête)</th>
                <th style={styles.th}>Abattage (FCFA/kg)</th>
                <th style={styles.th}>Ressuage (FCFA/unité)</th>
                <th style={styles.th}>Total ex. 100kg</th>
              </tr>
            </thead>
            <tbody>
              {especes.map(e => {
                const f = form[e.code]?.[typeSel] || { stabulation:'0', abattage:'0', ressuage:'0' };
                const stab = parseFloat(f.stabulation) || 0;
                const abat = parseFloat(f.abattage)    || 0;
                const ress = parseFloat(f.ressuage)    || 0;
                const exemple = stab + (abat * 100) + (ress * 100);
                return (
                  <tr key={e.code}>
                    <td style={styles.td}>
                      <span style={styles.badge(espaceColors[e.code] || '#6b7280')}>{e.code}</span>
                      {' '}{e.nom}
                    </td>
                    <td style={styles.td}>
                      <input type="number" min="0" style={styles.input}
                        value={f.stabulation}
                        onChange={ev => handleChange(e.code, 'stabulation', ev.target.value)} />
                    </td>
                    <td style={styles.td}>
                      <input type="number" min="0" style={styles.input}
                        value={f.abattage}
                        onChange={ev => handleChange(e.code, 'abattage', ev.target.value)} />
                    </td>
                    <td style={styles.td}>
                     <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                       <input type="number" min="0" style={styles.input}
                         value={f.ressuage}
                         onChange={ev => handleChange(e.code, 'ressuage', ev.target.value)} />
                       <select
                         style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, color: '#374151' }}
                         value={f.ressuage_unite || 'kg'}
                         onChange={ev => handleChange(e.code, 'ressuage_unite', ev.target.value)}
                       >
                         <option value="kg">/kg</option>
                         <option value="tete">/tête</option>
                       </select>
                      </div>
                     </td>
                    <td style={{ ...styles.td, ...styles.total }}>
                      {exemple.toLocaleString('fr-FR')} F
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>
                        stab + 100kg × (abat+ress)
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Viande Foraine */}
          <div style={styles.vfRow}>
            <span style={{ fontSize: 20 }}>🥩</span>
            <label style={styles.vfLabel}>Viande Foraine — Tarif unique (FCFA/kg)</label>
            <input type="number" min="0" style={{ ...styles.input, width: 120 }}
              value={vf}
              onChange={e => setVf(e.target.value)} />
            <span style={{ fontSize: 13, color: '#92400e' }}>
              Indépendant du type STANDARD/SPECIAL
            </span>
          </div>

          <button style={styles.btnSave} onClick={sauvegarder} disabled={sauvegarde}>
            {sauvegarde ? 'Sauvegarde...' : '💾 Sauvegarder les tarifs'}
          </button>
        </>
      )}
    </div>
  );
};

export default TarifsTab;
